/**
 * Retry Mechanisms with Exponential Backoff
 * Provides automatic and manual retry functionality for network failures and account setup
 * 
 * Requirements: 5.3, 5.1
 */

import { trackEvent } from '@/utils/analytics';
import logger from '@/utils/logger';
import { 
  ERROR_CATEGORIES, 
  ERROR_SEVERITY,
  trackErrorRecovery 
} from '@/utils/error-categorization';

/**
 * Retry configuration for different operation types
 */
const RETRY_CONFIG = {
  // Network operations
  network: {
    maxAttempts: 3,
    baseDelay: 1000, // 1 second
    maxDelay: 10000, // 10 seconds
    backoffMultiplier: 2,
    jitter: true
  },
  
  // Payment operations
  payment: {
    maxAttempts: 2,
    baseDelay: 2000, // 2 seconds
    maxDelay: 8000, // 8 seconds
    backoffMultiplier: 2,
    jitter: true
  },
  
  // Account creation operations
  account_creation: {
    maxAttempts: 3,
    baseDelay: 1500, // 1.5 seconds
    maxDelay: 12000, // 12 seconds
    backoffMultiplier: 2.5,
    jitter: true
  },
  
  // Twilio operations
  twilio: {
    maxAttempts: 2,
    baseDelay: 3000, // 3 seconds
    maxDelay: 10000, // 10 seconds
    backoffMultiplier: 2,
    jitter: false
  },
  
  // Authentication operations
  auth: {
    maxAttempts: 2,
    baseDelay: 1000, // 1 second
    maxDelay: 5000, // 5 seconds
    backoffMultiplier: 2,
    jitter: true
  }
};

/**
 * Retry Manager Class
 * Handles automatic retries with exponential backoff and manual retry tracking
 */
export class RetryManager {
  constructor(operationType = 'network') {
    this.operationType = operationType;
    this.config = RETRY_CONFIG[operationType] || RETRY_CONFIG.network;
    this.attemptHistory = [];
    this.isRetrying = false;
  }

  /**
   * Execute operation with automatic retry logic
   * @param {Function} operation - Async operation to execute
   * @param {Object} context - Operation context for logging
   * @returns {Promise} Operation result
   */
  async executeWithRetry(operation, context = {}) {
    const operationId = this.generateOperationId();
    let lastError;

    for (let attempt = 1; attempt <= this.config.maxAttempts; attempt++) {
      try {
        // Track attempt
        this.trackAttempt(operationId, attempt, context);

        // Execute operation
        const result = await operation();

        // Track success
        this.trackSuccess(operationId, attempt, context);

        return result;

      } catch (error) {
        lastError = error;
        
        // Track failure
        this.trackFailure(operationId, attempt, error, context);

        // Don't retry on final attempt
        if (attempt === this.config.maxAttempts) {
          break;
        }

        // Check if error is retryable
        if (!this.isRetryableError(error)) {
          logger.warn('Non-retryable error encountered', {
            operation_id: operationId,
            attempt,
            error: error.message,
            error_type: error.type || error.code
          });
          break;
        }

        // Calculate delay for next attempt
        const delay = this.calculateDelay(attempt);
        
        logger.log('Retrying operation after delay', {
          operation_id: operationId,
          attempt,
          next_attempt: attempt + 1,
          delay_ms: delay,
          error: error.message
        });

        // Wait before retry
        await this.sleep(delay);
      }
    }

    // All attempts failed
    const finalError = new Error(`Operation failed after ${this.config.maxAttempts} attempts: ${lastError.message}`);
    finalError.originalError = lastError;
    finalError.operationId = operationId;
    finalError.attempts = this.config.maxAttempts;
    
    throw finalError;
  }

  /**
   * Execute operation with manual retry capability
   * @param {Function} operation - Async operation to execute
   * @param {Object} context - Operation context
   * @returns {Promise} Operation result with retry info
   */
  async executeWithManualRetry(operation, context = {}) {
    const operationId = this.generateOperationId();
    
    try {
      this.trackAttempt(operationId, 1, context);
      const result = await operation();
      this.trackSuccess(operationId, 1, context);
      
      return {
        success: true,
        result,
        operationId,
        canRetry: false
      };
      
    } catch (error) {
      this.trackFailure(operationId, 1, error, context);
      
      return {
        success: false,
        error,
        operationId,
        canRetry: this.isRetryableError(error),
        retryFunction: () => this.executeWithManualRetry(operation, {
          ...context,
          retry_attempt: true,
          original_operation_id: operationId
        })
      };
    }
  }

  /**
   * Check if an error is retryable
   * @param {Error} error - Error to check
   * @returns {boolean} Whether error is retryable
   */
  isRetryableError(error) {
    const retryableErrors = [
      // Network errors
      ERROR_CATEGORIES.NETWORK_TIMEOUT,
      ERROR_CATEGORIES.NETWORK_CONNECTION_LOST,
      ERROR_CATEGORIES.NETWORK_SERVER_ERROR,
      
      // Temporary payment errors
      ERROR_CATEGORIES.PAYMENT_NETWORK_ERROR,
      ERROR_CATEGORIES.PAYMENT_PROCESSING_ERROR,
      
      // Temporary account creation errors
      ERROR_CATEGORIES.ACCOUNT_TWILIO_FAILED,
      ERROR_CATEGORIES.ACCOUNT_EMAIL_SERVICE_FAILED,
      
      // Temporary auth errors
      ERROR_CATEGORIES.AUTH_TOKEN_GENERATION_FAILED,
      ERROR_CATEGORIES.AUTH_SESSION_CREATION_FAILED
    ];

    // Check by error category if available
    if (error.category) {
      return retryableErrors.includes(error.category);
    }

    // Check by error message patterns
    const retryablePatterns = [
      /timeout/i,
      /network/i,
      /connection/i,
      /server error/i,
      /service unavailable/i,
      /rate limit/i,
      /temporary/i,
      /try again/i
    ];

    const errorMessage = error.message.toLowerCase();
    return retryablePatterns.some(pattern => pattern.test(errorMessage));
  }

  /**
   * Calculate delay for next retry attempt with exponential backoff
   * @param {number} attempt - Current attempt number
   * @returns {number} Delay in milliseconds
   */
  calculateDelay(attempt) {
    const exponentialDelay = this.config.baseDelay * Math.pow(this.config.backoffMultiplier, attempt - 1);
    const cappedDelay = Math.min(exponentialDelay, this.config.maxDelay);
    
    if (this.config.jitter) {
      // Add random jitter to prevent thundering herd
      const jitterAmount = cappedDelay * 0.1; // 10% jitter
      const jitter = (Math.random() - 0.5) * 2 * jitterAmount;
      return Math.max(0, cappedDelay + jitter);
    }
    
    return cappedDelay;
  }

  /**
   * Sleep for specified duration
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise} Promise that resolves after delay
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Track retry attempt
   * @param {string} operationId - Operation ID
   * @param {number} attempt - Attempt number
   * @param {Object} context - Operation context
   */
  trackAttempt(operationId, attempt, context) {
    const attemptData = {
      operation_id: operationId,
      operation_type: this.operationType,
      attempt_number: attempt,
      max_attempts: this.config.maxAttempts,
      timestamp: new Date().toISOString(),
      ...context
    };

    this.attemptHistory.push(attemptData);

    trackEvent('retry_attempt', attemptData);
    
    logger.log('Retry attempt started', attemptData);
  }

  /**
   * Track successful operation
   * @param {string} operationId - Operation ID
   * @param {number} attempt - Successful attempt number
   * @param {Object} context - Operation context
   */
  trackSuccess(operationId, attempt, context) {
    const successData = {
      operation_id: operationId,
      operation_type: this.operationType,
      successful_attempt: attempt,
      total_attempts: attempt,
      timestamp: new Date().toISOString(),
      ...context
    };

    trackEvent('retry_success', successData);
    
    logger.log('Retry operation successful', successData);
  }

  /**
   * Track failed attempt
   * @param {string} operationId - Operation ID
   * @param {number} attempt - Failed attempt number
   * @param {Error} error - Error that occurred
   * @param {Object} context - Operation context
   */
  trackFailure(operationId, attempt, error, context) {
    const failureData = {
      operation_id: operationId,
      operation_type: this.operationType,
      failed_attempt: attempt,
      error_message: error.message,
      error_type: error.type || error.code || 'unknown',
      is_retryable: this.isRetryableError(error),
      timestamp: new Date().toISOString(),
      ...context
    };

    trackEvent('retry_failure', failureData);
    
    logger.warn('Retry attempt failed', failureData);
  }

  /**
   * Generate unique operation ID
   * @returns {string} Operation ID
   */
  generateOperationId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 6);
    return `OP-${this.operationType.toUpperCase()}-${timestamp}-${random}`;
  }

  /**
   * Get attempt history for debugging
   * @returns {Array} Attempt history
   */
  getAttemptHistory() {
    return [...this.attemptHistory];
  }

  /**
   * Clear attempt history
   */
  clearHistory() {
    this.attemptHistory = [];
  }
}

/**
 * Specialized retry managers for different operation types
 */
export const networkRetryManager = new RetryManager('network');
export const paymentRetryManager = new RetryManager('payment');
export const accountCreationRetryManager = new RetryManager('account_creation');
export const twilioRetryManager = new RetryManager('twilio');
export const authRetryManager = new RetryManager('auth');

/**
 * Convenience functions for common retry operations
 */

/**
 * Retry network request with exponential backoff
 * @param {Function} requestFunction - Function that makes the network request
 * @param {Object} context - Request context
 * @returns {Promise} Request result
 */
export const retryNetworkRequest = (requestFunction, context = {}) => {
  return networkRetryManager.executeWithRetry(requestFunction, {
    request_type: 'network',
    ...context
  });
};

/**
 * Retry payment operation with appropriate backoff
 * @param {Function} paymentFunction - Function that processes payment
 * @param {Object} context - Payment context
 * @returns {Promise} Payment result
 */
export const retryPaymentOperation = (paymentFunction, context = {}) => {
  return paymentRetryManager.executeWithRetry(paymentFunction, {
    operation_type: 'payment',
    ...context
  });
};

/**
 * Retry account creation with manual fallback
 * @param {Function} accountFunction - Function that creates account
 * @param {Object} context - Account context
 * @returns {Promise} Account creation result with retry info
 */
export const retryAccountCreation = (accountFunction, context = {}) => {
  return accountCreationRetryManager.executeWithManualRetry(accountFunction, {
    operation_type: 'account_creation',
    ...context
  });
};

/**
 * Retry Twilio operation with appropriate delays
 * @param {Function} twilioFunction - Function that calls Twilio
 * @param {Object} context - Twilio context
 * @returns {Promise} Twilio result
 */
export const retryTwilioOperation = (twilioFunction, context = {}) => {
  return twilioRetryManager.executeWithRetry(twilioFunction, {
    operation_type: 'twilio',
    ...context
  });
};

/**
 * Retry authentication operation
 * @param {Function} authFunction - Function that handles authentication
 * @param {Object} context - Auth context
 * @returns {Promise} Auth result
 */
export const retryAuthOperation = (authFunction, context = {}) => {
  return authRetryManager.executeWithRetry(authFunction, {
    operation_type: 'auth',
    ...context
  });
};

/**
 * Background Job System for Failed Account Setups
 * Handles retry of failed account creation operations in the background
 */
export class BackgroundJobSystem {
  constructor() {
    this.jobQueue = [];
    this.isProcessing = false;
    this.processingInterval = null;
    this.maxJobAge = 24 * 60 * 60 * 1000; // 24 hours
  }

  /**
   * Queue failed account setup for background retry
   * @param {Object} accountData - Account setup data
   * @param {Error} error - Original error
   * @returns {string} Job ID
   */
  queueFailedAccountSetup(accountData, error) {
    const jobId = this.generateJobId();
    const job = {
      id: jobId,
      type: 'account_setup',
      data: accountData,
      error: {
        message: error.message,
        code: error.code || error.type,
        timestamp: new Date().toISOString()
      },
      attempts: 0,
      maxAttempts: 3,
      nextRetry: Date.now() + (5 * 60 * 1000), // Retry in 5 minutes
      createdAt: Date.now(),
      status: 'queued'
    };

    this.jobQueue.push(job);
    
    // Start processing if not already running
    this.startProcessing();

    // Track job queued
    trackEvent('background_job_queued', {
      job_id: jobId,
      job_type: job.type,
      account_email: accountData.email,
      original_error: error.message
    });

    logger.log('Background job queued for failed account setup', {
      job_id: jobId,
      account_email: accountData.email,
      next_retry: new Date(job.nextRetry).toISOString()
    });

    return jobId;
  }

  /**
   * Start background job processing
   */
  startProcessing() {
    if (this.processingInterval) {
      return; // Already processing
    }

    this.processingInterval = setInterval(() => {
      this.processJobs().catch(error => {
        logger.error('Error in background job processing', error);
      });
    }, 60000); // Check every minute

    logger.log('Background job processing started');
  }

  /**
   * Stop background job processing
   */
  stopProcessing() {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
      logger.log('Background job processing stopped');
    }
  }

  /**
   * Process queued jobs
   */
  async processJobs() {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      const now = Date.now();
      
      // Remove expired jobs
      this.jobQueue = this.jobQueue.filter(job => {
        const isExpired = (now - job.createdAt) > this.maxJobAge;
        if (isExpired) {
          this.trackJobExpired(job);
        }
        return !isExpired;
      });

      // Process ready jobs
      const readyJobs = this.jobQueue.filter(job => 
        job.status === 'queued' && job.nextRetry <= now
      );

      for (const job of readyJobs) {
        await this.processJob(job);
      }

    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Process individual job
   * @param {Object} job - Job to process
   */
  async processJob(job) {
    job.status = 'processing';
    job.attempts++;

    try {
      let result;
      
      switch (job.type) {
        case 'account_setup':
          result = await this.retryAccountSetup(job.data);
          break;
        default:
          throw new Error(`Unknown job type: ${job.type}`);
      }

      // Job succeeded
      job.status = 'completed';
      job.completedAt = Date.now();
      job.result = result;

      this.trackJobSuccess(job);

      // Remove completed job from queue
      this.jobQueue = this.jobQueue.filter(j => j.id !== job.id);

    } catch (error) {
      // Job failed
      job.lastError = {
        message: error.message,
        timestamp: new Date().toISOString()
      };

      if (job.attempts >= job.maxAttempts) {
        // Max attempts reached
        job.status = 'failed';
        job.failedAt = Date.now();
        
        this.trackJobFailed(job);
        
        // Remove failed job from queue
        this.jobQueue = this.jobQueue.filter(j => j.id !== job.id);
        
        // Notify administrators
        await this.notifyJobFailed(job);
        
      } else {
        // Schedule next retry with exponential backoff
        job.status = 'queued';
        const backoffDelay = Math.min(
          5 * 60 * 1000 * Math.pow(2, job.attempts - 1), // Exponential backoff starting at 5 minutes
          60 * 60 * 1000 // Max 1 hour
        );
        job.nextRetry = Date.now() + backoffDelay;
        
        this.trackJobRetry(job);
      }
    }
  }

  /**
   * Retry account setup operation
   * @param {Object} accountData - Account setup data
   * @returns {Promise} Account setup result
   */
  async retryAccountSetup(accountData) {
    // Create account creation function
    const createAccount = async () => {
      const response = await fetch('/api/auth/create-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(accountData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Account creation failed');
      }

      return await response.json();
    };

    // Use account creation retry manager
    return await accountCreationRetryManager.executeWithRetry(createAccount, {
      background_job: true,
      account_email: accountData.email,
      stripeCustomerId: accountData.stripeCustomerId
    });
  }

  /**
   * Notify administrators of job failure
   * @param {Object} job - Failed job
   */
  async notifyJobFailed(job) {
    // TODO: Integrate with admin notification system
    logger.error('Background job failed after max attempts', {
      job_id: job.id,
      job_type: job.type,
      attempts: job.attempts,
      account_email: job.data.email,
      last_error: job.lastError
    });

    trackEvent('background_job_failed', {
      job_id: job.id,
      job_type: job.type,
      attempts: job.attempts,
      account_email: job.data.email,
      failure_reason: job.lastError.message
    });
  }

  /**
   * Track job events
   */
  trackJobSuccess(job) {
    trackEvent('background_job_success', {
      job_id: job.id,
      job_type: job.type,
      attempts: job.attempts,
      processing_time: job.completedAt - job.createdAt
    });
  }

  trackJobFailed(job) {
    trackEvent('background_job_failed', {
      job_id: job.id,
      job_type: job.type,
      attempts: job.attempts,
      processing_time: job.failedAt - job.createdAt
    });
  }

  trackJobRetry(job) {
    trackEvent('background_job_retry', {
      job_id: job.id,
      job_type: job.type,
      attempt: job.attempts,
      next_retry: new Date(job.nextRetry).toISOString()
    });
  }

  trackJobExpired(job) {
    trackEvent('background_job_expired', {
      job_id: job.id,
      job_type: job.type,
      attempts: job.attempts,
      age_hours: (Date.now() - job.createdAt) / (60 * 60 * 1000)
    });
  }

  /**
   * Generate unique job ID
   * @returns {string} Job ID
   */
  generateJobId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 6);
    return `JOB-${timestamp}-${random}`.toUpperCase();
  }

  /**
   * Get job status
   * @param {string} jobId - Job ID
   * @returns {Object|null} Job status or null if not found
   */
  getJobStatus(jobId) {
    const job = this.jobQueue.find(j => j.id === jobId);
    if (!job) return null;

    return {
      id: job.id,
      type: job.type,
      status: job.status,
      attempts: job.attempts,
      maxAttempts: job.maxAttempts,
      nextRetry: job.nextRetry ? new Date(job.nextRetry).toISOString() : null,
      createdAt: new Date(job.createdAt).toISOString(),
      lastError: job.lastError
    };
  }

  /**
   * Get all job statuses
   * @returns {Array} Array of job statuses
   */
  getAllJobStatuses() {
    return this.jobQueue.map(job => this.getJobStatus(job.id));
  }
}

/**
 * Global background job system instance
 */
export const backgroundJobSystem = new BackgroundJobSystem();